/* eslint-disable i18next/no-literal-string */
import { pageDataService } from 'common/utilities';
import { Platform } from 'mcsclient/models';

import { BaseTheme } from '@suiteux/suiteux-shell-typings/typings/suiteux-shell';

import { AppSemanticColors, createAppSemanticColors, CustomSemanticColors, staticAppColors } from './AppSemanticColors';
import { CreateCustomTheme } from './Common';
import { FluentEffects, FluentPalette, FluentSemanticColors } from './ThemeTypes';

const isWebView = pageDataService.isWebView();

const lightThemeBackground = staticAppColors.anchorTint55;
const getBackgroundStyle = () => `${isWebView ? 'transparent' : lightThemeBackground} !important`;

const fluentPalette: FluentPalette = {
  // theme colors
  themePrimary: staticAppColors.bluePrimary, // *reviewed
  themeLighterAlt: '#f3f9fd',
  themeLighter: '#d0e7f8',
  themeLight: '#67737F', // *reviewed
  themeTertiary: '#5ca9e5',
  themeSecondary: '#1a86d9',
  themeDarkAlt: staticAppColors.blueShade10, // *reviewed
  themeDark: staticAppColors.blueShade20, // *reviewed
  themeDarker: staticAppColors.blueShade30, // *reviewed
  // neutrals
  neutralLighterAlt: '#faf9f8',
  neutralLighter: '#f3f2f1', // *reviewed === NeutralColors.gray20
  neutralLight: '#edebe9',
  neutralQuaternaryAlt: '#e1dfdd',
  neutralQuaternary: '#d0d0d0',
  neutralTertiaryAlt: '#c8c6c4', // *reviewed === grey60
  neutralTertiary: '#a19f9d',
  neutralSecondary: '#605e5c',
  neutralPrimaryAlt: '#3b3a39',
  neutralPrimary: '#323130',
  neutralDark: '#201f1e',
  // standard colors
  black: staticAppColors.neutralGray0,
  blackTranslucent40: 'rgba(0, 0, 0, 0.4)',
  white: staticAppColors.white100, // *reviewed
  whiteTranslucent40: staticAppColors.white40,
  green: staticAppColors.successPrimary,
};

const fluentSemanticColors: FluentSemanticColors = {
  bodyText: '#344554',
  bodySubtext: fluentPalette.themeLight,
  errorText: '#a4262c', // fluent default
  cardShadow: 'rgba(179, 191, 235, 1)',
  cardShadowHovered: 'rgba(179, 191, 235, 0.7)',
  // button
  buttonText: fluentPalette.themeDark,
  buttonTextHovered: fluentPalette.themeDarkAlt,
  buttonTextPressed: fluentPalette.themeDarker,
  buttonTextDisabled: staticAppColors.neutralGray82,
  buttonBackground: 'transparent',
  buttonBorder: fluentPalette.themePrimary,
  // primary button
  primaryButtonBackground: fluentPalette.themeDark,
  primaryButtonBackgroundDisabled: staticAppColors.neutralGray94,
  primaryButtonBackgroundHovered: fluentPalette.themeDarkAlt,
  primaryButtonBackgroundPressed: fluentPalette.themeDarker,
  primaryButtonBorder: fluentPalette.themePrimary,
  primaryButtonText: fluentPalette.white,
  primaryButtonTextDisabled: staticAppColors.neutralGray82,
  primaryButtonTextHovered: fluentPalette.white,
  primaryButtonTextPressed: fluentPalette.white,
  // link
  link: fluentPalette.themeDark,
  linkHovered: fluentPalette.themeDarkAlt,
};

const customAppColors: CustomSemanticColors = {
  // whites
  white100ToAnchor20: staticAppColors.white100,
  white90ToAnchor30tr90: staticAppColors.white90,
  white60ToAnchor30tr60: staticAppColors.white60,
  white60ToAnchor20tr60: staticAppColors.white60,
  white60ToAnchor20: staticAppColors.white60,
  white60ToAnchor10: staticAppColors.white60,
  white40ToBlack40: staticAppColors.white40,
  white40ToAnchor30tr40: staticAppColors.white40,
  white40ToAnchor20tr60: staticAppColors.white40,
  white30ToAnchor20tr60: staticAppColors.white30,
  // reds (critical colors)
  redPrimaryToRed10: staticAppColors.criticalPrimary,
  redPrimaryToCrimson70: staticAppColors.criticalPrimary,
  red600ToRedPrimary: staticAppColors.criticalRed600,
  red600ToCrimson70: staticAppColors.criticalRed600,
  red20ToRedPrimary: staticAppColors.criticalRed20,
  red20ToCrimson70: staticAppColors.criticalRed20,
  red20ToRedTint10: staticAppColors.criticalRed20,
  red20ToWhite: staticAppColors.criticalRed20,
  redTint50ToRed30: staticAppColors.criticalRedTint50,
  // greens (success colors)
  green20ToGreenPrimary: staticAppColors.successGreen20,
  greenBackgroundToSeafoam30: staticAppColors.successBackground,
  greenBackgroundToSeafoam30RGBA0: staticAppColors.successBackgroundRGBA0,
  greenBackgroundToSeafoam30RGBA100: staticAppColors.successBackgroundRGBA100,
  // grays (neutral colors)
  gray98ToAnchor30: staticAppColors.neutralGray98,
  gray98ToAnchor20tr60: staticAppColors.neutralGray98,
  gray94ToAnchor40: staticAppColors.neutralGray94,
  gray94ToAnchor20: staticAppColors.neutralGray94,
  gray94ToAnchor10: staticAppColors.neutralGray94,
  gray94ToTransparent: staticAppColors.neutralGray94,
  gray74ToAnchorTint20: staticAppColors.neutralGray74,
  gray38ToWhite: staticAppColors.neutralGray38,
  gray26ToGray94: staticAppColors.neutralGray26,
  gray14ToWhite: staticAppColors.neutralGray14,
  // blues
  bluePrimaryToWhite: staticAppColors.bluePrimary,
  blue20ToBluePrimary: staticAppColors.blueShade20,
  blue20ToBlueTint10: staticAppColors.blueShade20,
  blue20ToWhite: staticAppColors.blueShade20,
  blueTint40ToBlue10: staticAppColors.blueTint40,
  blueTint20ToBlue20: staticAppColors.blueTint20,
  anchorTint60ToAnchor30: staticAppColors.anchorTint60,
  anchorTint55ToAnchor40: staticAppColors.anchorTint55,
  // other
  neutralPrimaryToWhite: fluentPalette.neutralPrimary,

  // component-specific colors
  text: fluentSemanticColors.bodyText,
  textLight: fluentSemanticColors.bodySubtext,
  lightHeadlineText: '#2D3E4D',
  actionButtonText: fluentPalette.themeDark,
  pageBackground: getBackgroundStyle(),
  pageBackgroundColor: lightThemeBackground,
  pageFooterBackground: staticAppColors.white40,
  copilotPanelBackground: '#F3F3F3',
  calloutBackground: fluentSemanticColors.bodyText,
  horizontalLineColor: '#CCD9E4',
  inputBorderUnfocused: fluentPalette.neutralLighter,
  completionCountBackground: 'rgba(52, 69, 84, 0.12)', // fluentPalette.bodyText
  criticalRedBadge: staticAppColors.criticalRed20,
  deviceAlertTimelineFill: staticAppColors.blueTint40,
  // cards
  cardContentBackground: staticAppColors.white70,
  cardContentBackgroundHovered: '#F0F4FF',
  cardBorder: '#DCDCDC',
  cardBorderFocused: '#A7AECC',
  // dialog
  dialogBackground: getBackgroundStyle(),
  dialogBackgroundColor: lightThemeBackground,
  dialogBackdrop: isWebView ? 'transparent' : 'rgba(0, 0, 0, 0.6)',
  dialogOverlayBackground: fluentPalette.blackTranslucent40,
  dialogFooterBackground: staticAppColors.white40,
  dialogCloseIcon: '#212121',
  dialogCloseIconHover: '#212121',
  // list/table
  listBorder: '#E1E1E1',
  listBorderFocused: fluentSemanticColors.bodyText,
  listHeaderBackground: staticAppColors.white60,
  listHeaderText: '#2D3E4D',
  listFooterBackground: staticAppColors.white40,
  listZeroDataBackground: staticAppColors.white60,
  // tags
  greenTagBackground: staticAppColors.successBackground,
  yellowTagBackground: staticAppColors.neutralGray96,
  redTagBackground: staticAppColors.criticalRedTint60,
  defaultTagBackground: staticAppColors.blueGray75,
  redTagText: staticAppColors.criticalRed20,
  greenTagBorder: 'transparent',
  redTagBorder: 'transparent',
  // icons
  themeIconDefault: fluentSemanticColors.bodyText,
  iconCircleBackground: staticAppColors.neutralGray94,
  // scrollbar
  customScrollbarThumb: fluentPalette.neutralTertiaryAlt,
  customScrollbarThumbHover: '#2D3E4D', // same as lightHeadlineText
};

const effects: FluentEffects = {
  elevation2: '0px 1px 2px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
  elevation4: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
  elevation8: '0px 4px 8px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
  elevation16: '0px 8px 16px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)',
  elevation28: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
  elevation64: '0px 32px 64px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
};

const appSemanticColors: AppSemanticColors = createAppSemanticColors(customAppColors);

export const LightTheme = (webViewPlatform: Platform | undefined) =>
  CreateCustomTheme(webViewPlatform, fluentPalette, fluentSemanticColors, appSemanticColors, effects);

export const LightShellAppBrandTheme: BaseTheme = {
  Primary: staticAppColors.transparent,
  Accent: '#4599FF',
  AppName: 'rgba(0, 0, 0, 1)',
  DefaultText: 'rgba(0, 0, 0, 1)!important',
  HoverText: 'rgba(0, 0, 0, 1)',
  HoverBackground: '#D9DCF8',
  PressedText: 'rgba(0, 0, 0, 1)',
  PressedBackground: '#DDE0F9',
  SelectedText: 'rgba(0, 0, 0, 1)',
  SelectedBackground: '#FFFFFF',
};
